<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <AdminPageHeader
      title="Appointment Slots"
      subtitle="Manage available appointment time slots"
      icon="fas fa-calendar-alt"
      gradient="blue"
    />

    <!-- Always-visible action buttons -->
    <div class="d-flex justify-content-end mb-3">
      <div class="d-flex gap-2">
        <BButton
          variant="outline-primary"
          size="sm"
          @click="createMultipleSlots"
        >
          <i class="fas fa-layer-group me-1" />
          Add Multiple
        </BButton>
        <BButton
          variant="primary"
          size="sm"
          @click="createSlot"
        >
          <i class="fas fa-plus me-1" />
          Add Slot
        </BButton>
        <BButton
          variant="outline-secondary"
          size="sm"
          @click="openEditSlots"
        >
          <i class="fas fa-edit me-1" />
          Edit Slots
        </BButton>
      </div>
    </div>

    <!-- Calendar Navigation -->
    <BCard class="border shadow-sm mb-3">
      <BCardText>
        <SlotCalendar
          :slots="allSlots"
          :show-slots-inline="true"
          :initial-date="calendarInitialDate"
          @month-change="onCalendarMonthChange"
        />
      </BCardText>
    </BCard>

    <!-- Add/Edit Slot Modal -->
    <BModal
      ref="modalForm"
      size="lg"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      :title="state.editing ? 'Edit Slot' : 'Add Slot'"
    >
      <ValidationObserver ref="formData">
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>
            <BCol
              cols="12"
              md="4"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="date"
                name="date"
                rules="required"
              >
                <BFormGroup
                  label="Date"
                  label-for="fDate"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fDate"
                    v-model="formData.date"
                    type="date"
                    autocomplete="off"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="4"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="start_time"
                name="start time"
                rules="required"
              >
                <BFormGroup
                  label="Start Time"
                  label-for="fStartTime"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fStartTime"
                    v-model="formData.start_time"
                    type="time"
                    autocomplete="off"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="4"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="end_time"
                name="end time"
                rules="required"
              >
                <BFormGroup
                  label="End Time"
                  label-for="fEndTime"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fEndTime"
                    v-model="formData.end_time"
                    type="time"
                    autocomplete="off"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="notification_recipient_email"
                name="notification email"
                rules="email"
              >
                <BFormGroup
                  label="Notification Email"
                  label-for="fEmail"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fEmail"
                    v-model="formData.notification_recipient_email"
                    placeholder="enter notification email"
                    type="email"
                    autocomplete="off"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div class="mt-2 d-flex align-items-center gap-2 flex-wrap">
                    <BButton
                      size="sm"
                      variant="outline-success"
                      :disabled="state.busy || singleOtp.sending || singleOtp.cooldown > 0 || !formData.notification_recipient_email"
                      @click="sendSingleOtp"
                    >
                      <i :class="['fas me-1', singleOtp.sending ? 'fa-spinner fa-spin' : 'fa-paper-plane']" />
                      {{ singleOtp.sending ? 'Sending OTP...' : 'Send OTP' }}
                    </BButton>

                    <small
                      v-if="singleOtp.cooldown > 0"
                      class="text-muted"
                    >
                      Resend available in {{ singleOtp.cooldown }}s
                    </small>
                  </div>

                  <div
                    v-if="singleOtp.sent && !singleOtp.verified"
                    class="mt-2 d-flex align-items-center gap-2 flex-wrap"
                  >
                    <BFormInput
                      v-model.trim="singleOtp.code"
                      type="text"
                      maxlength="6"
                      placeholder="Enter 6-digit OTP"
                      :disabled="state.busy || singleOtp.verifying"
                      style="max-width: 220px;"
                    />

                    <BButton
                      size="sm"
                      variant="success"
                      :disabled="state.busy || singleOtp.verifying || singleOtp.code.length !== 6"
                      @click="verifySingleOtp"
                    >
                      <i :class="['fas me-1', singleOtp.verifying ? 'fa-spinner fa-spin' : 'fa-check']" />
                      {{ singleOtp.verifying ? 'Verifying...' : 'Verify OTP' }}
                    </BButton>
                  </div>

                  <small
                    v-if="singleOtp.success"
                    class="text-success d-block mt-2"
                  >
                    <i class="fas fa-check-circle me-1" />
                    {{ singleOtp.success }}
                  </small>

                  <small
                    v-if="singleOtp.error"
                    class="text-danger d-block mt-2"
                  >
                    <i class="fas fa-exclamation-circle me-1" />
                    {{ singleOtp.error }}
                  </small>

                  <small
                    v-if="requiresSingleOtpVerification"
                    class="text-warning d-block mt-2"
                  >
                    <i class="fas fa-shield-alt me-1" />
                    Verify this email via OTP before saving.
                  </small>
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              v-if="state.editing"
              cols="12"
              md="6"
            >
              <BFormGroup class="mb-2 mt-md-4">
                <BFormCheckbox
                  id="is_available"
                  v-model="formData.is_available"
                  switch
                  value="1"
                  name="is_available"
                  unchecked-value="0"
                  :disabled="state.busy"
                >
                  Is Available?
                </BFormCheckbox>
              </BFormGroup>
            </BCol>
          </BRow>
        </form>
      </ValidationObserver>

      <template #modal-footer="{ cancel }">
        <BButton
          variant="primary"
          :disabled="state.busy"
          @click.prevent="validateForm"
        >
          {{ state.editing ? 'Update' : 'Create' }}
        </BButton>
        <BButton
          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>
    </BModal>

    <!-- Edit Slots Modal -->
    <BModal
      ref="modalEditSlots"
      size="lg"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      title="Edit Slots for Date"
    >
      <div class="mb-3 d-flex gap-2 align-items-center">
        <BFormInput
          v-model="editSlotsDate"
          type="date"
          :disabled="state.busy || editLoading"
        />
        <BButton
          size="sm"
          variant="primary"
          :disabled="state.busy || editLoading"
          @click="fetchEditSlots"
        >
          <i class="fas fa-search me-1" /> Load
        </BButton>
      </div>

      <div
        v-if="editLoading"
        class="text-center py-2"
      >
        <i class="fas fa-spinner fa-spin me-1" /> Loading...
      </div>

      <div v-else>
        <div
          v-if="editSlots.length === 0"
          class="text-muted"
        >
          No slots for this date
        </div>
        <div
          v-for="slot in editSlots"
          :key="slot.id"
          class="slot-card mb-2 p-2"
        >
          <BRow class="align-items-center">
            <BCol cols="4">
              <BFormInput
                v-model="slot.start_time"
                type="time"
                :disabled="state.busy"
              />
            </BCol>
            <BCol cols="4">
              <BFormInput
                v-model="slot.end_time"
                type="time"
                :disabled="state.busy"
              />
            </BCol>
            <BCol cols="2">
              <BFormCheckbox
                v-model="slot.is_available"
                switch
                :disabled="state.busy"
              >
                Available
              </BFormCheckbox>
            </BCol>
            <BCol
              cols="2"
              class="text-end"
            >
              <BButton
                size="sm"
                variant="primary"
                :disabled="state.busy"
                @click="saveSlot(slot)"
              >
                Save
              </BButton>
            </BCol>
          </BRow>
        </div>
      </div>

      <template #modal-footer="{ cancel }">
        <BButton
          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Close
        </BButton>
      </template>
    </BModal>

    <!-- Multi-Slot Creation Modal -->
    <BModal
      ref="modalMultiSlot"
      size="lg"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      title="Bulk Slot Creation"
    >
      <!-- Generation Mode Selection -->
      <div class="mb-4">
        <label class="font-weight-bolder mb-2">Generation Mode</label>
        <div class="d-flex gap-2 flex-wrap">
          <BButton
            :variant="batchFormData.mode === 'day' ? 'primary' : 'outline-secondary'"
            size="sm"
            @click="batchFormData.mode = 'day'"
          >
            <i class="fas fa-calendar-day me-1" />
            Single Day
          </BButton>
          <BButton
            :variant="batchFormData.mode === 'range' ? 'primary' : 'outline-secondary'"
            size="sm"
            @click="batchFormData.mode = 'range'"
          >
            <i class="fas fa-calendar-week me-1" />
            Date Range
          </BButton>

        </div>
      </div>

      <!-- Date Selection Based on Mode -->
      <BRow class="mb-3">
        <BCol
          v-if="batchFormData.mode === 'day'"
          cols="12"
          md="6"
        >
          <BFormGroup
            label="Date"
            label-for="bulkDate"
            label-class="font-weight-bolder"
          >
            <BFormInput
              id="bulkDate"
              v-model="batchFormData.date"
              type="date"
              :disabled="state.busy"
            />
          </BFormGroup>
        </BCol>

        <template v-if="batchFormData.mode === 'range'">
          <BCol
            cols="12"
            md="6"
          >
            <BFormGroup
              label="Start Date"
              label-for="bulkStartDate"
              label-class="font-weight-bolder"
            >
              <BFormInput
                id="bulkStartDate"
                v-model="batchFormData.startDate"
                type="date"
                :disabled="state.busy"
              />
            </BFormGroup>
          </BCol>
          <BCol
            cols="12"
            md="6"
          >
            <BFormGroup
              label="End Date"
              label-for="bulkEndDate"
              label-class="font-weight-bolder"
            >
              <BFormInput
                id="bulkEndDate"
                v-model="batchFormData.endDate"
                type="date"
                :disabled="state.busy"
              />
            </BFormGroup>
          </BCol>
        </template>

        <BCol
          cols="12"
          md="6"
        >
          <BFormGroup
            label="Notification Email (Optional)"
            label-for="batchEmail"
            label-class="font-weight-bolder"
          >
            <BFormInput
              id="batchEmail"
              v-model="batchFormData.notification_recipient_email"
              placeholder="enter notification email"
              type="email"
              autocomplete="off"
              :disabled="state.busy"
            />

            <div class="mt-2 d-flex align-items-center gap-2 flex-wrap">
              <BButton
                size="sm"
                variant="outline-success"
                :disabled="state.busy || batchOtp.sending || batchOtp.cooldown > 0 || !batchFormData.notification_recipient_email"
                @click="sendBatchOtp"
              >
                <i :class="['fas me-1', batchOtp.sending ? 'fa-spinner fa-spin' : 'fa-paper-plane']" />
                {{ batchOtp.sending ? 'Sending OTP...' : 'Send OTP' }}
              </BButton>

              <small
                v-if="batchOtp.cooldown > 0"
                class="text-muted"
              >
                Resend available in {{ batchOtp.cooldown }}s
              </small>
            </div>

            <div
              v-if="batchOtp.sent && !batchOtp.verified"
              class="mt-2 d-flex align-items-center gap-2 flex-wrap"
            >
              <BFormInput
                v-model.trim="batchOtp.code"
                type="text"
                maxlength="6"
                placeholder="Enter 6-digit OTP"
                :disabled="state.busy || batchOtp.verifying"
                style="max-width: 220px;"
              />

              <BButton
                size="sm"
                variant="success"
                :disabled="state.busy || batchOtp.verifying || batchOtp.code.length !== 6"
                @click="verifyBatchOtp"
              >
                <i :class="['fas me-1', batchOtp.verifying ? 'fa-spinner fa-spin' : 'fa-check']" />
                {{ batchOtp.verifying ? 'Verifying...' : 'Verify OTP' }}
              </BButton>
            </div>

            <small
              v-if="batchOtp.success"
              class="text-success d-block mt-2"
            >
              <i class="fas fa-check-circle me-1" />
              {{ batchOtp.success }}
            </small>

            <small
              v-if="batchOtp.error"
              class="text-danger d-block mt-2"
            >
              <i class="fas fa-exclamation-circle me-1" />
              {{ batchOtp.error }}
            </small>

            <small
              v-if="requiresBatchOtpVerification"
              class="text-warning d-block mt-2"
            >
              <i class="fas fa-shield-alt me-1" />
              Verify this email via OTP before creating slots.
            </small>
          </BFormGroup>
        </BCol>
      </BRow>

      <!-- Days of Week (only for range mode) -->
      <div
        v-if="batchFormData.mode === 'range'"
        class="mb-3"
      >
        <label class="font-weight-bolder mb-2">Apply to Days</label>
        <div class="d-flex gap-2 flex-wrap">
          <BFormCheckbox
            v-for="(day, idx) in weekDayOptions"
            :key="idx"
            v-model="batchFormData.selectedDays"
            :value="idx"
            button
            :button-variant="batchFormData.selectedDays.includes(idx) ? 'primary' : 'outline-secondary'"
            size="sm"
          >
            {{ day }}
          </BFormCheckbox>
        </div>
      </div>

      <!-- Time Slots -->
      <div class="mb-3">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <label class="font-weight-bolder mb-0">Time Slots Per Day</label>
          <BButton
            variant="outline-primary"
            size="sm"
            @click="addBatchSlotRow"
          >
            <i class="fas fa-plus me-1" />
            Add Slot
          </BButton>
        </div>

        <div
          v-for="(slot, index) in batchFormData.slots"
          :key="index"
          class="batch-slot-row mb-2"
        >
          <BRow class="align-items-center">
            <BCol cols="5">
              <BFormInput
                v-model="slot.start_time"
                type="time"
                placeholder="Start Time"
                :disabled="state.busy"
              />
            </BCol>
            <BCol cols="5">
              <BFormInput
                v-model="slot.end_time"
                type="time"
                placeholder="End Time"
                :disabled="state.busy"
              />
            </BCol>
            <BCol cols="2">
              <BButton
                variant="outline-danger"
                size="sm"
                :disabled="state.busy || batchFormData.slots.length <= 1"
                @click="removeBatchSlotRow(index)"
              >
                <i class="fas fa-times" />
              </BButton>
            </BCol>
          </BRow>
        </div>
      </div>

      <!-- Preview -->
      <div
        v-if="bulkPreviewCount > 0"
        class="alert alert-info py-2"
      >
        <i class="fas fa-info-circle me-1" />
        This will create approximately <strong>{{ bulkPreviewCount }}</strong> slot(s)
      </div>

      <template #modal-footer="{ cancel }">
        <BButton
          variant="primary"
          :disabled="state.busy || batchFormData.slots.length === 0"
          @click.prevent="validateBatchForm"
        >
          <i :class="['fas me-1', state.busy ? 'fa-spinner fa-spin' : 'fa-layer-group']" />
          Create Slots
        </BButton>
        <BButton
          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>
    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'

import AdminPageHeader from '@/components/core/AdminPageHeader.vue'
import SlotCalendar from '@/components/custom/SlotCalendar.vue'
import { SAdminAppointmentEmailOtp, SAdminAppointmentSlot } from '@/services'

export default {
  name: 'VAdminAppointmentSlots',

  components: { AdminPageHeader, SlotCalendar },

  mixins: [MxMisc, MxFormatter],

  middleware: ['auth', 'admin'],

  metaInfo: () => ({ title: 'Appointment Slots' }),

  data () {
    const now = new Date()
    return {
      state: { busy: false, editing: false, loadingDay: false },
      singleOtpTimerId: null,
      batchOtpTimerId: null,
      originalSingleNotificationEmail: '',
      calendarInitialDate: now,
      currentDateFrom: '',
      currentDateTo: '',
      selectedDate: null,
      allSlots: [],
      formData: {
        id: '', date: '', start_time: '', end_time: '', is_available: 1, notification_recipient_email: ''
      },
      batchFormData: {
        mode: 'day', // 'day', 'range'
        date: '',
        startDate: '',
        endDate: '',
        selectedDays: [1, 2, 3, 4, 5], // Mon-Fri by default (for range mode)
        notification_recipient_email: '',
        slots: [{ start_time: '08:00', end_time: '09:00' }]
      },
      singleOtp: {
        sending: false,
        sent: false,
        verifying: false,
        verified: false,
        code: '',
        token: '',
        verifiedEmail: '',
        cooldown: 0,
        success: '',
        error: ''
      },
      batchOtp: {
        sending: false,
        sent: false,
        verifying: false,
        verified: false,
        code: '',
        token: '',
        verifiedEmail: '',
        cooldown: 0,
        success: '',
        error: ''
      },
      // edit slots modal state
      editSlotsDate: '',
      editSlots: [],
      editLoading: false,
      weekDayOptions: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      monthOptions: [
        { value: 0, text: 'January' },
        { value: 1, text: 'February' },
        { value: 2, text: 'March' },
        { value: 3, text: 'April' },
        { value: 4, text: 'May' },
        { value: 5, text: 'June' },
        { value: 6, text: 'July' },
        { value: 7, text: 'August' },
        { value: 8, text: 'September' },
        { value: 9, text: 'October' },
        { value: 10, text: 'November' },
        { value: 11, text: 'December' }
      ]
    }
  },

  computed: {
    ...mapGetters({ currentUser: 'auth/user' }),

    normalizedSingleNotificationEmail () {
      return this.normalizeEmail(this.formData.notification_recipient_email)
    },

    normalizedBatchNotificationEmail () {
      return this.normalizeEmail(this.batchFormData.notification_recipient_email)
    },

    requiresSingleOtpVerification () {
      if (!this.normalizedSingleNotificationEmail) return false

      if (!this.state.editing) {
        return true
      }

      return this.normalizedSingleNotificationEmail !== this.originalSingleNotificationEmail
    },

    requiresBatchOtpVerification () {
      return !!this.normalizedBatchNotificationEmail
    },

    bulkPreviewCount () {
      const { mode, date, startDate, endDate, selectedDays, slots } = this.batchFormData
      const validSlots = slots.filter(s => s.start_time && s.end_time && s.end_time > s.start_time).length
      if (validSlots === 0) return 0

      if (mode === 'day') {
        return date ? validSlots : 0
      }

      if (mode === 'range') {
        if (!startDate || !endDate || endDate < startDate) return 0
        let count = 0
        const start = new Date(startDate + 'T00:00:00')
        const end = new Date(endDate + 'T00:00:00')
        const current = new Date(start)
        // eslint-disable-next-line no-unmodified-loop-condition
        while (current <= end) {
          if (selectedDays.includes(current.getDay())) count++
          current.setDate(current.getDate() + 1)
        }
        return count * validSlots
      }

      return 0
    }
  },

  watch: {
    normalizedSingleNotificationEmail (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.resetOtpState('single')
      }
    },

    normalizedBatchNotificationEmail (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.resetOtpState('batch')
      }
    }
  },

  beforeDestroy () {
    this.clearOtpTimer('single')
    this.clearOtpTimer('batch')
  },

  mounted () {
    const now = new Date()
    this.fetchMonthSlots({
      dateFrom: this.toDateString(new Date(now.getFullYear(), now.getMonth(), 1)),
      dateTo: this.toDateString(new Date(now.getFullYear(), now.getMonth() + 1, 0))
    })
  },

  methods: {
    toDateString (d) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },

    normalizeEmail (value) {
      return (value || '').trim().toLowerCase()
    },

    clearOtpTimer (scope) {
      const timerKey = scope === 'single' ? 'singleOtpTimerId' : 'batchOtpTimerId'
      if (this[timerKey]) {
        clearInterval(this[timerKey])
        this[timerKey] = null
      }
    },

    startOtpCooldown (scope, seconds = 60) {
      const otpState = scope === 'single' ? this.singleOtp : this.batchOtp
      this.clearOtpTimer(scope)

      otpState.cooldown = seconds

      const timerKey = scope === 'single' ? 'singleOtpTimerId' : 'batchOtpTimerId'
      this[timerKey] = setInterval(() => {
        otpState.cooldown = Math.max(0, otpState.cooldown - 1)
        if (otpState.cooldown === 0) {
          this.clearOtpTimer(scope)
        }
      }, 1000)
    },

    resetOtpState (scope) {
      const otpState = scope === 'single' ? this.singleOtp : this.batchOtp
      otpState.sent = false
      otpState.verified = false
      otpState.code = ''
      otpState.token = ''
      otpState.verifiedEmail = ''
      otpState.success = ''
      otpState.error = ''
      otpState.verifying = false
      this.clearOtpTimer(scope)
      otpState.cooldown = 0
    },

    async sendOtpForScope (scope, email) {
      const otpState = scope === 'single' ? this.singleOtp : this.batchOtp
      otpState.error = ''
      otpState.success = ''

      const normalizedEmail = this.normalizeEmail(email)
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

      if (!emailRegex.test(normalizedEmail)) {
        otpState.error = 'Please enter a valid email address before requesting OTP.'
        return
      }

      otpState.sending = true

      try {
        const { data } = await SAdminAppointmentEmailOtp.send(normalizedEmail)
        otpState.sent = true
        otpState.verified = false
        otpState.code = ''
        otpState.token = ''
        otpState.verifiedEmail = ''
        otpState.success = data?.message || 'OTP sent successfully.'
        this.startOtpCooldown(scope, 60)
      } catch (error) {
        otpState.error = error?.response?.data?.message || 'Failed to send OTP.'
      } finally {
        otpState.sending = false
      }
    },

    async verifyOtpForScope (scope, email, otp) {
      const otpState = scope === 'single' ? this.singleOtp : this.batchOtp
      otpState.error = ''
      otpState.success = ''

      const normalizedEmail = this.normalizeEmail(email)
      const code = (otp || '').trim()

      if (!/^\d{6}$/.test(code)) {
        otpState.error = 'Please enter the 6-digit OTP.'
        return
      }

      otpState.verifying = true

      try {
        const { data } = await SAdminAppointmentEmailOtp.verify(normalizedEmail, code)
        otpState.verified = true
        otpState.token = data?.data?.verification_token || ''
        otpState.verifiedEmail = normalizedEmail
        otpState.success = data?.message || 'Email verified successfully.'
      } catch (error) {
        otpState.verified = false
        otpState.token = ''
        otpState.verifiedEmail = ''
        otpState.error = error?.response?.data?.message || 'Invalid OTP.'
      } finally {
        otpState.verifying = false
      }
    },

    async sendSingleOtp () {
      await this.sendOtpForScope('single', this.formData.notification_recipient_email)
    },

    async verifySingleOtp () {
      await this.verifyOtpForScope('single', this.formData.notification_recipient_email, this.singleOtp.code)
    },

    async sendBatchOtp () {
      await this.sendOtpForScope('batch', this.batchFormData.notification_recipient_email)
    },

    async verifyBatchOtp () {
      await this.verifyOtpForScope('batch', this.batchFormData.notification_recipient_email, this.batchOtp.code)
    },

    formatTime (time) {
      return time ? time.substring(0, 5) : ''
    },

    onCalendarMonthChange ({ dateFrom, dateTo }) {
      this.fetchMonthSlots({ dateFrom, dateTo })
    },

    async fetchMonthSlots ({ dateFrom, dateTo } = {}) {
      this.state.loadingDay = true
      const resolvedDateFrom = dateFrom || this.currentDateFrom
      const resolvedDateTo = dateTo || this.currentDateTo

      if (!resolvedDateFrom || !resolvedDateTo) {
        this.state.loadingDay = false
        return
      }

      this.currentDateFrom = resolvedDateFrom
      this.currentDateTo = resolvedDateTo

      try {
        const { data } = await SAdminAppointmentSlot.getCalendar({
          date_from: resolvedDateFrom,
          date_to: resolvedDateTo,
          per_page: 500
        })
        this.allSlots = data.items || []
      } catch {
        this.allSlots = []
      } finally {
        this.state.loadingDay = false
      }
    },

    createSlot () {
      this.state.editing = false
      this.formData = {
        id: '',
        date: '',
        start_time: '',
        end_time: '',
        is_available: 1,
        notification_recipient_email: ''
      }
      this.originalSingleNotificationEmail = ''
      this.resetOtpState('single')
      this.$refs.modalForm.show()
    },

    editSlot (slot) {
      this.state.editing = true
      this.formData = {
        id: slot.id,
        date: slot.date,
        start_time: slot.start_time ? slot.start_time.substring(0, 5) : '',
        end_time: slot.end_time ? slot.end_time.substring(0, 5) : '',
        is_available: slot.is_available,
        notification_recipient_email: slot.notification_recipient_email || ''
      }
      this.originalSingleNotificationEmail = this.normalizeEmail(slot.notification_recipient_email)
      this.resetOtpState('single')
      this.$refs.modalForm.show()
    },

    async validateForm () {
      await this.$refs.formData.validate().then(async valid => {
        if (!valid) return this.swalInvalid()

        if (this.requiresSingleOtpVerification) {
          const email = this.normalizedSingleNotificationEmail
          const isVerified = this.singleOtp.verified && this.singleOtp.token && this.singleOtp.verifiedEmail === email

          if (!isVerified) {
            return this.swalInvalid('Please verify the notification email via OTP before saving.')
          }
        }

        const payload = {
          ...this.formData,
          notification_recipient_email: this.formData.notification_recipient_email || null,
          otp_verification_token: this.requiresSingleOtpVerification ? this.singleOtp.token : null
        }

        this.swalConfirm({
          html: this.state.editing ? '<h6>Update this Slot?</h6>' : '<h6>Create this Slot?</h6>',
          confirmButtonText: this.state.editing ? 'Update' : 'Create',
          preConfirm: async () => {
            this.state.busy = true
            const op = this.state.editing
              ? SAdminAppointmentSlot.update(this.formData.id, payload)
              : SAdminAppointmentSlot.store(payload)
            return op.then(({ data: { message } }) => {
              this.swalSuccess(message).then(() => {
                this.$refs.modalForm.hide()
                this.fetchMonthSlots()
                this.resetOtpState('single')
              })
            }).catch(({ message }) => {
              if (message?.errors) this.$refs.formData.setErrors(message.errors)
            }).finally(() => { this.state.busy = false })
          }
        })
      })
    },

    createMultipleSlots () {
      const now = new Date()
      this.batchFormData = {
        mode: 'day',
        date: this.toDateString(now),
        startDate: this.toDateString(now),
        endDate: this.toDateString(new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)),
        selectedDays: [1, 2, 3, 4, 5],
        notification_recipient_email: '',
        slots: [{ start_time: '08:00', end_time: '09:00' }]
      }
      this.resetOtpState('batch')
      this.$refs.modalMultiSlot.show()
    },

    addBatchSlotRow () {
      this.batchFormData.slots.push({ start_time: '', end_time: '' })
    },

    removeBatchSlotRow (index) {
      if (this.batchFormData.slots.length > 1) {
        this.batchFormData.slots.splice(index, 1)
      }
    },

    generateBulkDates () {
      const { mode, date, startDate, endDate, selectedDays } = this.batchFormData
      const dates = []

      if (mode === 'day' && date) {
        dates.push(date)
      } else if (mode === 'range' && startDate && endDate) {
        const start = new Date(startDate + 'T00:00:00')
        const end = new Date(endDate + 'T00:00:00')
        const current = new Date(start)
        // eslint-disable-next-line no-unmodified-loop-condition
        while (current <= end) {
          if (selectedDays.includes(current.getDay())) {
            dates.push(this.toDateString(current))
          }
          current.setDate(current.getDate() + 1)
        }
      }

      return dates
    },

    async validateBatchForm () {
      const validSlots = this.batchFormData.slots.filter(s => s.start_time && s.end_time)
      if (validSlots.length === 0) {
        return this.swalInvalid('Please add at least one valid time slot')
      }

      if (this.requiresBatchOtpVerification) {
        const email = this.normalizedBatchNotificationEmail
        const isVerified = this.batchOtp.verified && this.batchOtp.token && this.batchOtp.verifiedEmail === email

        if (!isVerified) {
          return this.swalInvalid('Please verify the notification email via OTP before creating slots.')
        }
      }

      const invalidSlots = validSlots.filter(s => s.start_time >= s.end_time)
      if (invalidSlots.length > 0) {
        return this.swalInvalid('End time must be after start time')
      }

      const dates = this.generateBulkDates()
      if (dates.length === 0) {
        return this.swalInvalid('No valid dates selected')
      }

      const totalSlots = dates.length * validSlots.length
      this.swalConfirm({
        html: `<h6>Create ${totalSlots} slot(s) across ${dates.length} day(s)?</h6>`,
        confirmButtonText: 'Create',
        preConfirm: async () => {
          this.state.busy = true
          try {
            let created = 0
            let skipped = 0
            const errors = []

            // Process in chunks to avoid timeout
            for (const date of dates) {
              try {
                const { data } = await SAdminAppointmentSlot.storeBatch({
                  date,
                  slots: validSlots,
                  notification_recipient_email: this.batchFormData.notification_recipient_email || null,
                  otp_verification_token: this.requiresBatchOtpVerification ? this.batchOtp.token : null
                })
                if (data?.data) created += data.data.length
                if (data?.skipped?.length) skipped += data.skipped.length
                if (data?.errors?.length) errors.push(...data.errors)
              } catch (err) {
                const errorMsg = err?.response?.data?.message || err?.response?.data?.error || `Failed for ${date}`
                errors.push(errorMsg)
              }
            }

            if (created === 0 && errors.length > 0) {
              throw new Error(errors[0])
            }

            const summary = []
            summary.push(`${created} slot(s) created successfully`)
            if (skipped > 0) summary.push(`${skipped} skipped (already existing)`)
            if (errors.length > 0) summary.push(`${errors.length} failed`)
            const msg = summary.join(', ')
            this.swalSuccess(msg).then(() => {
              this.$refs.modalMultiSlot.hide()
              this.fetchMonthSlots()
              this.resetOtpState('batch')
            })
          } catch (error) {
            this.swalError(error?.message || 'Failed to create slots')
          } finally {
            this.state.busy = false
          }
        }
      })
    },

    async deleteSlotConfirm (slot) {
      const result = await this.$swal({
        title: 'Delete Slot',
        text: `Delete slot on ${slot.date} (${this.formatTime(slot.start_time)} - ${this.formatTime(slot.end_time)})?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      })
      if (result.isConfirmed) {
        this.state.busy = true
        try {
          await SAdminAppointmentSlot.destroy(slot.id)
          this.swalSuccess('Slot deleted successfully')
          this.fetchMonthSlots()
        } catch (error) {
          this.swalError(error.response?.data?.message || 'Failed to delete')
        } finally { this.state.busy = false }
      }
    },

    async openEditSlots () {
      const now = new Date()
      this.editSlotsDate = this.toDateString(now)
      this.editSlots = []
      this.$refs.modalEditSlots.show()
      await this.fetchEditSlots()
    },

    async fetchEditSlots () {
      if (!this.editSlotsDate) return
      this.editLoading = true
      try {
        const { data } = await SAdminAppointmentSlot.getCalendar({
          date_from: this.editSlotsDate,
          date_to: this.editSlotsDate,
          per_page: 500
        })
        this.editSlots = (data.items || []).map(s => ({ ...s }))
      } catch (err) {
        this.editSlots = []
        this.swalError('Failed to load slots for that date')
      } finally {
        this.editLoading = false
      }
    },

    async saveSlot (slot) {
      this.state.busy = true
      try {
        const payload = {
          start_time: slot.start_time,
          end_time: slot.end_time,
          is_available: slot.is_available,
          notification_recipient_email: slot.notification_recipient_email || null
        }
        await SAdminAppointmentSlot.update(slot.id, payload)
        this.swalSuccess('Slot updated').then(() => this.fetchMonthSlots())
      } catch (err) {
        this.swalError(err.response?.data?.message || 'Failed to update slot')
      } finally {
        this.state.busy = false
      }
    }
  }
}
</script>

<style scoped>
/* Day detail slot cards - same visual as booking page */
.slot-card {
  background: #fff;
  border-color: #e2e8f0 !important;
  min-width: 250px;
  flex: 1 1 250px;
  max-width: 400px;
  transition: all 0.15s;
}
.slot-card:hover {
  border-color: #3b82f6 !important;
  box-shadow: 0 1px 4px rgba(59, 130, 246, 0.12);
}
.slot-card.slot-unavailable {
  background: #f8fafc;
  opacity: 0.7;
}
.slot-time {
  font-size: 0.9rem;
  color: #334155;
}

.batch-slot-row {
  padding: 8px;
  background: #f8fafc;
  border-radius: 6px;
}
</style>
